<script setup>
import { langTypes } from "@/utils/constant"
import ContentError from "@/components/common/ContentError.vue"
import BtnLang from "@/components/BtnLang.vue"
definePage({
  meta: {
    layout: "blank",
    unauthenticatedOnly: false,
  },
})
import StudentService from "@/services/StudentService"
import { $gettext } from "@/services/ApiService"
import { useCustomSwal } from "@/composables/usePopup"
const { showCustomSwal } = useCustomSwal()

const errorData = ref(null)
const info = ref({})
const route = useRoute()
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR || 2026
const isLoading = ref(false)
const isDisabled = ref(false)
const isRefresh = ref(false)

const onSubmit = async (obj) => {
  isDisabled.value = true
  try {
    await StudentService.updateRegister({
      ...obj,
      programId: info.value?.program?.id,
    })
    await showCustomSwal({
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
      timerProgressBar: true,
      timer: 10000,
      width: 490,
      position: "center",
      title: $gettext("Données enregistrées avec succès"),
      text: $gettext(
        "Un email contenant un lien de participation vous a été envoyé."
      ),
      footer: `<div>
        ${$gettext("Si vous ne le recevez pas : consultez vos spams.")}
        </div>
        ${$gettext("Si vous n'avez rien dans vos spams,")} 
        <div>
          <b>${$gettext("NE VOUS RÉINSCRIVEZ PAS")}</b> 
          ${$gettext("et contactez-nous par e-mail à :")}
        </div>
        <div><a href='mailto:classements.france@eduniversal-group.com'>classements.france@eduniversal-group.com</a></div>
        `,
    }).then(() => {
      isDisabled.value = false
    })
  } catch (error) {
    errorData.value = error?.data
    if (error?.data?.message) {
      await showCustomSwal({
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
        timerProgressBar: true,
        timer: 10000,
        width: 490,
        position: "center",
        // icon: "error",
        html: `<div class="font-weight-regular">${error?.data?.message}</div>`,
      }).then(() => {
        isDisabled.value = false
      })
    }
  }
}

const onChangeLang = (lang) => {
  const locale = langTypes.find((x) => x.id === lang)
  if (locale) {
    const urlObj = new URL(window.location.href)
    urlObj.searchParams.set("lang", locale.name) // Replace 'en' with your desired language
    const newUrl = urlObj.toString()
    window.location.href = newUrl
  }
}

onMounted(async () => {
  const { params } = route
  try {
    const res = await StudentService.getRegister(params.id)
    info.value = res || {}
  } catch (error) {
    errorData.value = error?.data
  }
})
</script>

<template>
  <div class="register-student">
    <div class="register-student__container" style="max-width: 900px">
      <div class="register-student__bg">
        <img src="@/assets/images/palm_bg.svg" alt="palm" />
      </div>
      <!-- Start Header -->
      <header
        class="d-flex align-center justify-space-between register-student__container-header"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img
            src="@/assets/logo-eduniversal.png"
            alt="Logo"
            style="max-width: 140px"
          />
        </RouterLink>
        <div></div>
        <BtnLang @onChange="onChangeLang" />
      </header>
      <main
        class="register-student__main py-6 px-6 px-md-12"
        style="background: #fff"
      >
        <div>
          <h1 v-if="info?.program?.type === 2">
            {{ $gettext("Enquêtes") }} {{ $gettext("Eduniversal") }} {{ CURRENT_YEAR }}
            <div style="font-size: 1.85rem; color: #cb9f6e">
              {{ $gettext("des") }} {{ $gettext("Meilleurs Licences, Bachelors") }}
              {{ $gettext("et") }} {{ $gettext("Grandes Écoles") }}
            </div>
          </h1>
          <h1 v-else>
            {{ $gettext("Enquêtes") }} {{ $gettext("Eduniversal") }} {{ CURRENT_YEAR }}
            <div style="font-size: 1.85rem; color: #cb9f6e">
              {{ $gettext("des") }} {{ $gettext("Meilleurs Masters, MS") }}
              {{ $gettext("et") }} {{ $gettext("MBA") }}
            </div>
          </h1>
        </div>

        <hr class="my-4" />
        <div style="max-width: 700px; margin: 0 auto">
          <ContentError v-if="errorData?.code" :code="errorData?.code" />
          <template v-else>
            <div class="form__title">
              <h4>
                {{ $gettext("Votre formation en") }}
                {{ CURRENT_YEAR - 1 }}/{{ CURRENT_YEAR }}
              </h4>
              <h3>
                {{ info.program?.school?.name }}
              </h3>
              <h3>
                {{ info.program?.name }}
              </h3>
            </div>
            <FormRegisterStudent
              v-if="!isRefresh"
              :loading="isLoading"
              :disabled="isDisabled"
              @update:modelValue="onSubmit"
              :message="errorData && errorData?.message"
              class="mt-4"
            />
          </template>
        </div>
      </main>
      <div
        class="d-flex align-center bg-white px-6 pt-6 pb-10 mt-6"
        style="position: relative; border-radius: 16px"
      >
        <h3 class="text-primary" style="width: 400px">
          {{ $gettext("Encore besoin d'aide ?") }}
        </h3>
        <div>
          <div>
            <b>
              {{
                $gettext(
                  "Nos spécialistes sont toujours heureux de vous aider."
                )
              }}
            </b>
          </div>
          {{
            $gettext(
              "Contactez-nous pendant les heures normales de bureau ou envoyez-nous un e-mail 24h/24 et 7j/7 et nous vous répondrons."
            )
          }}
        </div>
      </div>

      <div class="text-center" style="margin-top: -18px">
        <v-btn
          href="mailto:classements.france@eduniversal-group.com"
          style="border-radius: 36px"
        >
          {{ $gettext("Nous contacter") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/pages/register-student.scss";
</style>
